import { Button } from '@consigli/shadcn';
import clsx from 'clsx';
import { FC, useState } from 'react';

import { Sheet } from './sheet';
import { errorToast } from '../toast';

interface SheetFormProps {
  title: string;
  description?: string;
  trigger: React.ReactNode;
  triggerClassName?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
  children: React.ReactNode;
  onSubmit?: () => Promise<boolean> | boolean | void;
  onCancel?: () => void;
  onReset?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  resetLabel?: string;
  isSubmitting?: boolean;
  showReset?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isUploading?: boolean;
}

export const SheetForm: FC<SheetFormProps> = ({
  title,
  description,
  trigger,
  children,
  side = 'right',
  triggerClassName,
  className,
  onSubmit,
  onCancel,
  onReset,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  resetLabel = 'Reset',
  isSubmitting = false,
  showReset = false,
  open,
  onOpenChange,
  isUploading = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = open !== undefined ? open : internalOpen;
  const handleOpenChange = onOpenChange || setInternalOpen;

  const handleSubmit = async () => {
    if (onSubmit) {
      const result = await onSubmit();
      if (result === true || result === undefined) {
        handleOpenChange(false);
      }
    } else {
      errorToast({ message: 'No onSubmit handler provided' });
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleOpenChange(false);
  };

  return (
    <Sheet
      isOpen={isOpen}
      handleOpenChange={handleOpenChange}
      triggerClassName={triggerClassName}
      trigger={trigger}
      side={side}
      className={clsx('px-2 py-5 w-full sm:max-w-2xl', className)}
      title={title}
      description={description}
      footer={
        <>
          {showReset && onReset && (
            <Button type="button" variant="outline" onClick={onReset} disabled={isSubmitting}>
              {resetLabel}
            </Button>
          )}
          {onCancel && (
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              {cancelLabel}
            </Button>
          )}
          {onSubmit && (
            <Button type="button" onClick={handleSubmit} disabled={isSubmitting || isUploading}>
              {isSubmitting ? 'Submitting...' : submitLabel}
            </Button>
          )}
        </>
      }
    >
      {children}
    </Sheet>
  );
};
